import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '../icons';

interface VideoEmbedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (title: string, url: string) => void;
}

function parseVideoEmbedUrl(url: string): string {
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  return url;
}

export function VideoEmbedModal({ isOpen, onClose, onAdd }: VideoEmbedModalProps) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  const handleSubmit = () => {
    if (!title.trim() || !url.trim()) return;
    const embedUrl = parseVideoEmbedUrl(url.trim());
    onAdd(title.trim(), embedUrl);
    setTitle('');
    setUrl('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
              <h3 className="freud-heading-base text-chocolate-900">Add Video</h3>
              <button onClick={onClose} className="p-1.5 hover:bg-cream-100 rounded-lg transition-colors">
                <X className="w-4 h-4 text-chocolate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Introduction to Module 1"
                  className="w-full border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Video URL</label>
                <input
                  type="text"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="YouTube or Vimeo link"
                  className="w-full border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400"
                />
                <p className="text-xs text-chocolate-400 mt-1">Supports YouTube and Vimeo links (including unlisted)</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-cream-200 bg-cream-50">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-chocolate-600 hover:bg-cream-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!title.trim() || !url.trim()}
                className="freud-btn-primary text-sm disabled:opacity-40"
              >
                Add Video
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
